function createStableId(prefix) {
  const uuid = globalThis.crypto?.randomUUID?.()
  return uuid
    ? `${prefix}_${uuid}`
    : `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`
}

export const emptyDefectLine = () => ({
  line_id: createStableId('line'),
  room_joinery: '',
  room_location: null,
  flashing_location: '',
  size_code: '',
  done: false,
  same_room_as_above: false,
})

export const emptyDefectUnit = () => ({
  unit_id: createStableId('unit'),
  unit_lot: '',
  lines: [emptyDefectLine()],
})

function isGroupedUnit(row) {
  return row && typeof row === 'object' && Array.isArray(row.lines)
}

function normalizeUnitLines(lines) {
  const source = Array.isArray(lines) && lines.length > 0 ? lines : [emptyDefectLine()]
  const result = []

  for (let i = 0; i < source.length; i += 1) {
    const r = source[i] && typeof source[i] === 'object' ? source[i] : {}
    const code = String(r.size_code ?? '').trim().toUpperCase()
    let sameRoom = i > 0 && !!r.same_room_as_above
    let room = String(r.room_joinery ?? '').trim()

    if (i > 0 && !Object.prototype.hasOwnProperty.call(r, 'same_room_as_above')) {
      const prevRoom = result[i - 1].room_joinery
      if (room && room === prevRoom) sameRoom = true
    }

    if (i > 0 && sameRoom) {
      room = result[i - 1].room_joinery
    }

    result.push({
      line_id: String(r.line_id || '').trim() || createStableId('line'),
      room_joinery: room,
      room_location: r.room_location == null ? null : String(r.room_location).trim(),
      flashing_location: String(r.flashing_location ?? '').trim(),
      size_code: ['HF', 'FF', 'DF'].includes(code) ? code : '',
      done: !!r.done,
      same_room_as_above: i > 0 ? sameRoom : false,
    })
  }

  return result
}

function normalizeLine(row) {
  return normalizeUnitLines([row])[0]
}

/** Load API items (grouped or legacy flat rows) into unit groups. */
export function normalizeUnitsFromApi(raw) {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [emptyDefectUnit()]
  }

  if (raw.some(isGroupedUnit)) {
    return raw.map((unit) => {
      const u = unit && typeof unit === 'object' ? unit : {}
      const lines = Array.isArray(u.lines) && u.lines.length > 0
        ? normalizeUnitLines(u.lines)
        : [emptyDefectLine()]
      return {
        unit_id: String(u.unit_id || '').trim() || createStableId('unit'),
        unit_lot: String(u.unit_lot ?? '').trim(),
        lines,
      }
    })
  }

  return raw.map((row) => {
    const r = row && typeof row === 'object' ? row : {}
    return {
      unit_id: String(r.unit_id || '').trim() || createStableId('unit'),
      unit_lot: String(r.unit_lot ?? '').trim(),
      lines: [normalizeLine(r)],
    }
  })
}

export function countDefectUnits(items) {
  return normalizeUnitsFromApi(items).length
}

export function countDefectLines(items) {
  return normalizeUnitsFromApi(items).reduce(
    (sum, unit) => sum + (Array.isArray(unit.lines) ? unit.lines.length : 0),
    0
  )
}

export function countDoneDefectLines(items) {
  return normalizeUnitsFromApi(items).reduce(
    (sum, unit) =>
      sum + (Array.isArray(unit.lines) ? unit.lines.filter((line) => line?.done).length : 0),
    0
  )
}
